import { useCallback, useContext, useState } from "react";
import { Dataset, DatasetFieldLabels } from "../../../api/Datasets";
import { DataFormat, DataFormatFilters } from "../../../api/DataFormats";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import { Tile } from "../../../common/panels/Tile/Tile";
import { ContentTable } from "../../../common/tables/ContentTable/ContentTable";
import { DatasetContext } from "../../Dataset";
import styles from "./SourceInformation.module.css";
import { useEntityDetail, useUnpaginate } from "../../../api/BaseEntityApi";
import { getLoadingState, LoadingWrapper } from "../../../common/LoadingWrapper";
import { NotAvailable, NotSet } from "../../../common/misc/UIconstants";
import { dataSourceConstants } from "../../../api/DataSource";
import { LinkEntity } from "../../../common/misc/LinkEntity/LinkEntity";
import { UID } from "../../../common/badges/UID/UID";
import { IconRenderer } from "../../../common/icon/IconRenderer";
import { Vendor, VendorFilters, vendorsConstants } from "../../../api/Vendors";
import { RenderArray } from "../../../common/misc/EntityRenders/EntityRenderer";

const DataSource = ({ dataset }: { dataset: Dataset }) => {
  const context = useContext(DatasetContext);
  if (dataset.autouploadServer) {
    if (context?.viewShared)
      return (
        <div className={"container_label"} style={{ gap: 5 }}>
          <LucideIcon name={dataSourceConstants.icon} color={"var(--gray-500)"} style={{ flexShrink: 0 }} />
          <div className={"container_label_name"}>
            <span>{dataset.autouploadServer.name}</span>
          </div>
        </div>
      );
    return <LinkEntity entityConstants={dataSourceConstants} property={dataset.autouploadServer} />;
  }

  if (dataset.origin && dataset.origin.uid)
    return (
      <div className={"container_label"} style={{ gap: 5 }}>
        <LucideIcon name="git-pull-request" color={"var(--gray-500)"} style={{ flexShrink: 0 }} />
        <div className={"container_label_name"}>
          <span>{dataset.origin.name}</span>
        </div>
        <div className={"container_label_id"}>
          <UID placement="right" entity={{ id: dataset.origin.id, uid: dataset.origin.uid }} />
        </div>
      </div>
    );

  switch (dataset.source.type) {
    case "ForeignServer":
      return (
        <div className={"container_label"} style={{ gap: 5 }}>
          <LucideIcon name="server" color={"var(--gray-500)"} style={{ flexShrink: 0 }} />
          <div className={"container_label_name"}>
            <span>{dataset.source.name}</span>
          </div>
        </div>
      );
    case "AutoUpload":
      if (context?.viewShared)
        return (
          <div className={"container_label"} style={{ gap: 5 }}>
            <LucideIcon name={dataSourceConstants.icon} color={"var(--gray-500)"} style={{ flexShrink: 0 }} />
            <div className={"container_label_name"}>
              <span>{dataset.source.name}</span>
            </div>
          </div>
        );
      return <LinkEntity entityConstants={dataSourceConstants} property={dataset.source} />;
    case "ManualUpload":
      return (
        <div className={"container_label"} style={{ gap: 5 }}>
          <LucideIcon name="upload" color={"var(--gray-500)"} style={{ flexShrink: 0 }} />
          <div className={"container_label_name"}>
            <span>Manual file upload</span>
          </div>
        </div>
      );
    case "Unknown":
      return (
        <div className={"container_label"} style={{ gap: 5 }}>
          <LucideIcon name="upload" color={"var(--gray-500)"} style={{ flexShrink: 0 }} />
          <div className={"container_label_name"}>
            <span>Unknown</span>
          </div>
        </div>
      );
  }
};

interface SourceInformationProps {
  dataset: Dataset;
  tile?: boolean;
}
export const SourceInformation = ({ dataset, tile = true }: SourceInformationProps) => {
  const context = useContext(DatasetContext);
  const [textAreaHeight, setTextAreaHeight] = useState<number>();
  const ref = useCallback((node: HTMLTextAreaElement) => {
    if (node) {
      setTextAreaHeight(node.scrollHeight);
    }
  }, []);

  const parsersQuery = useEntityDetail<DataFormat, DataFormatFilters>(
    "data_formats",
    dataset.format?.id!,
    {},
    {
      enabled: !!dataset.format?.id && !context?.viewShared,
    }
  );
  const { data: parser } = parsersQuery;

  const vendorsQuery = useUnpaginate<Vendor, VendorFilters>(
    vendorsConstants.resource,
    { ids: parser?.vendors?.map((v) => v.id), includeIcon: true },
    {
      enabled: parser && !!Array.isArray(parser.vendors) && !context?.viewShared,
    }
  );

  const { data: vendors } = vendorsQuery;

  const { status, fetchStatus, error } = getLoadingState([parsersQuery, vendorsQuery]);

  const content = (
    <LoadingWrapper status={status} fetchStatus={fetchStatus} error={error}>
      <ContentTable
        content={[
          ...(Array.isArray(dataset.importExportEvents) && !!dataset.importExportEvents.length
            ? [
                {
                  key: "Import/Export Events",
                  value: (
                    <RenderArray values={dataset.importExportEvents} style={{ width: "100%" }}>
                      {(value, index) => (
                        <div
                          className="flex row-nowrap gap-5 ellipsisContainer"
                          title={value.eventType === "Import" ? "Import Event" : "Export Event"}
                        >
                          <span>
                            <LucideIcon
                              name={value.eventType === "Import" ? "file-down" : "file-up"}
                              color={"var(--primary)"}
                              style={{ flexShrink: 0 }}
                            />{" "}
                            {value.foreignUrl ? (
                              <a href={value.foreignUrl} target="_blank" rel="noopener noreferrer">
                                {value.foreignStorage?.name ?? value.foreignUrl}
                              </a>
                            ) : (
                              value.foreignStorage?.name
                            )}
                          </span>
                          {value.foreignUid && (
                            <div className={"container_label_id"} style={{ marginLeft: "auto" }}>
                              <UID placement="right" entity={{ id: 0, uid: value.foreignUid }} />
                            </div>
                          )}
                        </div>
                      )}
                    </RenderArray>
                  ),
                },
              ]
            : []),
          {
            key: "Origin",
            value: <DataSource dataset={dataset} />,
          },
          {
            key: DatasetFieldLabels.automaticName,
            value: (
              <div className="ellipsisContainer">
                <span>{dataset.automaticName ?? NotSet}</span>
              </div>
            ),
          },
          {
            key: DatasetFieldLabels.format,
            value: (
              <>
                {
                  <div className={"flex row-nowrap gap-5"} style={{ width: "100%" }}>
                    <span>{dataset.format?.name ?? NotAvailable}</span>
                    <div className={"container_label_id"} style={{ marginLeft: "auto" }}>
                      <span>{dataset.format?.id ?? NotAvailable}</span>
                    </div>
                  </div>
                }
              </>
            ),
          },
          ...(vendors && !!Object.keys(vendors).length
            ? [
                {
                  key: "Vendor",
                  value: (
                    <div className={"flex col-nowrap gap-5"} style={{ width: "100%" }}>
                      {parser?.vendors?.map((v) => (
                        <div className="flex row-nowrap gap-5" key={v.id}>
                          <span>{v.name}</span>
                          <div style={{ marginLeft: "auto" }}>
                            {vendors && <IconRenderer src={vendors[v.id]?.icon} />}
                          </div>
                        </div>
                      )) ?? NotSet}
                    </div>
                  ),
                },
              ]
            : []),
          {
            key: "Path",
            value: dataset.source.type === "ManualUpload" ? NotAvailable : "",
          },
        ]}
      />
      {dataset.source.type !== "ManualUpload" && (
        <div className={styles.path}>
          <textarea
            className={styles.path_textarea}
            ref={ref}
            value={dataset.path}
            disabled
            style={{ height: textAreaHeight }}
          />
        </div>
      )}
    </LoadingWrapper>
  );

  if (!tile) return content;
  return (
    <Tile>
      <Tile.Head
        title={
          <>
            <LucideIcon name={dataSourceConstants.icon} /> Source information
          </>
        }
      />
      <Tile.Body>{content}</Tile.Body>
    </Tile>
  );
};
